<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import {
  Bell,
  BriefcaseBusiness,
  CalendarCheck2,
  ChevronLeft,
  ChevronRight,
  FileStack,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Menu,
  LoaderCircle,
  RefreshCw,
  Settings,
  UserRoundCheck,
  WifiOff,
  X,
} from "lucide-vue-next";
import LogoMark from "@/components/LogoMark.vue";
import PersonAvatar from "@/components/people/PersonAvatar.vue";
import NotificationDrawer from "@/components/workspace/NotificationDrawer.vue";
import WorkspaceSearch from "@/components/workspace/WorkspaceSearch.vue";
import { useEmulators } from "@/services/firebase";
import { useWorkspaceStore } from "@/stores/workspace";

const props = defineProps<{ projectMode?: boolean }>();
const workspace = useWorkspaceStore();
const route = useRoute();
const router = useRouter();
const collapsed = ref(false);
const mobileOpen = ref(false);
const showNotifications = ref(false);
const showProfile = ref(false);
const profileArea = ref<HTMLElement | null>(null);
const profileButton = ref<HTMLButtonElement | null>(null);
const mobileRail = ref<HTMLElement | null>(null);
const mobileClose = ref<HTMLButtonElement | null>(null);
let mobileReturnFocus: HTMLElement | null = null;
let mobilePriorOverflow = "";
const demoMode = computed(() => useEmulators || workspace.usingDemo);
const isWorkspaceAdministrator = computed(
  () => workspace.user?.role === "Workspace administrator",
);
const workspaceUpdateLabel = computed(() =>
  workspace.workspaceLiveState === "paused"
    ? "Workspace updates paused"
    : "Connecting to workspace updates",
);

const nav = computed(() => [
  { label: "Home", to: "/projects?section=home", icon: LayoutDashboard },
  { label: "My Work", to: "/my-work", icon: CalendarCheck2 },
  { label: "Projects", to: "/projects", icon: FolderKanban },
  ...(workspace.isInternal
    ? [{ label: "Templates", to: "/templates", icon: FileStack }]
    : []),
  ...(isWorkspaceAdministrator.value
    ? [
        {
          label: "Administration",
          to: "/administration",
          icon: Settings,
        },
      ]
    : []),
]);

function isNavActive(item: { to: string }) {
  const [path, query] = item.to.split("?");
  if (route.path !== path) return false;
  if (query) return route.query.section === query.split("=")[1];
  if (path === "/projects") return !route.query.section;
  return true;
}

async function logout() {
  showProfile.value = false;
  sessionStorage.removeItem("planner-demo-profile");
  await workspace.logout();
  router.replace("/login");
}

async function retryWorkspaceUpdates() {
  await workspace.retryWorkspaceRealtime();
}

async function toggleProfile() {
  showProfile.value = !showProfile.value;
  if (showProfile.value) {
    await nextTick();
    profileArea.value?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }
}

function closeProfile(returnFocus = false) {
  showProfile.value = false;
  if (returnFocus) profileButton.value?.focus();
}

function handleProfileKeys(event: KeyboardEvent) {
  if (!showProfile.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    closeProfile(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  const items = [
    ...(profileArea.value?.querySelectorAll<HTMLElement>('[role="menuitem"]') ||
      []),
  ];
  if (!items.length) return;
  event.preventDefault();
  const currentIndex = items.indexOf(document.activeElement as HTMLElement);
  if (event.key === "Home") items[0]?.focus();
  else if (event.key === "End") items.at(-1)?.focus();
  else if (event.key === "ArrowDown")
    items[(currentIndex + 1 + items.length) % items.length]?.focus();
  else items[(currentIndex - 1 + items.length) % items.length]?.focus();
}

function handleDocumentPointer(event: PointerEvent) {
  if (showProfile.value && !profileArea.value?.contains(event.target as Node))
    closeProfile();
}

watch(mobileOpen, async (open) => {
  if (open) {
    mobileReturnFocus = document.activeElement as HTMLElement;
    mobilePriorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    await nextTick();
    mobileClose.value?.focus();
  } else {
    document.body.style.overflow = mobilePriorOverflow;
    mobileReturnFocus?.focus();
  }
});
watch(
  () => route.fullPath,
  () => closeProfile(),
);
onMounted(() =>
  document.addEventListener("pointerdown", handleDocumentPointer),
);
onBeforeUnmount(() => {
  document.body.style.overflow = mobilePriorOverflow;
  document.removeEventListener("pointerdown", handleDocumentPointer);
});

function trapMobileKeys(event: KeyboardEvent) {
  if (!mobileOpen.value || !mobileRail.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    mobileOpen.value = false;
    return;
  }
  if (event.key !== "Tab") return;
  const items = [
    ...mobileRail.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!items.length) return;
  const first = items[0]!;
  const last = items.at(-1)!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
</script>

<template>
  <div class="app-shell" :class="{ 'app-shell--collapsed': collapsed }">
    <aside
      ref="mobileRail"
      class="rail"
      :class="{ 'rail--mobile-open': mobileOpen }"
      :role="mobileOpen ? 'dialog' : undefined"
      :aria-modal="mobileOpen ? 'true' : undefined"
      aria-label="Workspace navigation"
      @keydown="trapMobileKeys"
    >
      <div class="rail__head">
        <LogoMark />
        <button
          ref="mobileClose"
          class="rail__mobile-close"
          type="button"
          aria-label="Close navigation"
          @click="mobileOpen = false"
        >
          <X :size="20" />
        </button>
      </div>
      <nav class="rail__nav" aria-label="Workspace destinations">
        <RouterLink
          v-for="item in nav"
          :key="item.label"
          :to="item.to"
          class="rail-link"
          :class="{ 'rail-link--active': isNavActive(item) }"
          :aria-current="isNavActive(item) ? 'page' : undefined"
          @click="mobileOpen = false"
        >
          <component :is="item.icon" :size="18" aria-hidden="true" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>
      <div class="rail__footer">
        <div class="workspace-chip">
          <span class="avatar avatar--brand">VD</span>
          <span><strong>VIPdesk</strong><small>Workspace</small></span>
        </div>
        <button
          class="rail-collapse"
          type="button"
          :aria-label="collapsed ? 'Expand navigation' : 'Collapse navigation'"
          @click="collapsed = !collapsed"
        >
          <ChevronRight v-if="collapsed" :size="18" /><ChevronLeft
            v-else
            :size="18"
          />
          <span>{{ collapsed ? "Expand" : "Collapse" }}</span>
        </button>
      </div>
    </aside>

    <header class="topbar">
      <button
        class="icon-button mobile-menu"
        type="button"
        aria-label="Open navigation"
        @click="mobileOpen = true"
      >
        <Menu :size="21" />
      </button>
      <WorkspaceSearch />
      <div class="topbar__actions">
        <div
          v-if="workspace.workspaceLiveState !== 'live'"
          class="workspace-update-state"
          :class="`workspace-update-state--${workspace.workspaceLiveState}`"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          :aria-label="workspaceUpdateLabel"
        >
          <WifiOff
            v-if="workspace.workspaceLiveState === 'paused'"
            :size="14"
            aria-hidden="true"
          />
          <LoaderCircle
            v-else
            :size="14"
            class="workspace-update-state__spinner"
            aria-hidden="true"
          />
          <span>{{ workspaceUpdateLabel }}</span>
          <button
            v-if="workspace.workspaceLiveState === 'paused'"
            type="button"
            :disabled="workspace.reconcilingWorkspace"
            @click="retryWorkspaceUpdates"
          >
            <RefreshCw :size="13" aria-hidden="true" />
            {{ workspace.reconcilingWorkspace ? "Retrying" : "Retry" }}
          </button>
        </div>
        <span
          v-if="demoMode"
          class="demo-label"
          title="Synthetic data and local demo identities"
          ><span class="demo-label__wide">Synthetic demo</span
          ><span class="demo-label__compact">Demo</span></span
        >
        <button
          class="icon-button notification-button"
          type="button"
          aria-label="Open notifications"
          @click="showNotifications = true"
        >
          <Bell :size="19" /><span
            v-if="workspace.unreadCount"
            aria-hidden="true"
            >{{ workspace.unreadCount }}</span
          >
        </button>
        <div
          ref="profileArea"
          class="profile-area"
          @keydown="handleProfileKeys"
        >
          <button
            ref="profileButton"
            class="profile-button"
            type="button"
            aria-haspopup="menu"
            :aria-expanded="showProfile"
            @click="toggleProfile"
          >
            <PersonAvatar
              :name="workspace.user?.displayName || 'VIPdesk'"
              :initials="workspace.user?.initials || 'VD'"
              :photo-url="workspace.user?.photoUrl"
            />
            <span class="profile-button__copy"
              ><strong>{{ workspace.user?.displayName || "VIPdesk" }}</strong
              ><small>{{ workspace.user?.role || "Workspace" }}</small></span
            >
          </button>
          <div v-if="showProfile" class="profile-menu" role="menu">
            <div>
              <strong>{{ workspace.user?.displayName }}</strong
              ><small>{{ workspace.user?.email }}</small>
            </div>
            <RouterLink to="/profile" role="menuitem" @click="closeProfile()">
              <UserRoundCheck :size="17" /> Profile and preferences
            </RouterLink>
            <button type="button" role="menuitem" @click="logout">
              <LogOut :size="17" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="app-main" :class="{ 'app-main--project': props.projectMode }">
      <slot />
    </main>
    <button
      v-if="mobileOpen"
      class="scrim"
      type="button"
      aria-label="Close navigation"
      @click="mobileOpen = false"
    ></button>
    <NotificationDrawer
      :open="showNotifications"
      @close="showNotifications = false"
    />
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
  padding-left: var(--rail-width);
  padding-top: var(--top-height);
}
.app-shell--collapsed {
  padding-left: 72px;
}
.rail {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 40;
  width: var(--rail-width);
  display: flex;
  flex-direction: column;
  color: white;
  background: var(--ink-900);
}
.app-shell--collapsed .rail {
  width: 72px;
}
.rail__head {
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.11);
  overflow: hidden;
}
.rail__mobile-close {
  display: none;
  background: transparent;
  color: white;
}
.rail__nav {
  display: grid;
  gap: 3px;
  padding: 16px 10px;
}
.rail-link {
  min-height: 42px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  color: #c6d3dc;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
}
.rail-link:hover {
  background: rgba(255, 255, 255, 0.075);
  color: white;
}
.rail-link--active,
.rail-link[aria-current="page"] {
  background: rgba(124, 173, 62, 0.17);
  color: #f2f7ea;
  box-shadow: inset 2px 0 0 var(--brand);
}
.rail__footer {
  margin-top: auto;
  padding: 12px 10px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}
.workspace-chip {
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 7px 8px;
  white-space: nowrap;
}
.workspace-chip > span:last-child {
  display: grid;
}
.workspace-chip small {
  color: #9db0be;
}
.rail-collapse {
  min-height: 36px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-radius: 5px;
  background: transparent;
  color: #aebec9;
  cursor: pointer;
  white-space: nowrap;
}
.rail-collapse:hover {
  background: rgba(255, 255, 255, 0.07);
  color: white;
}
.app-shell--collapsed .rail :deep(.logo-mark__product),
.app-shell--collapsed .rail-link span,
.app-shell--collapsed .workspace-chip > span:last-child,
.app-shell--collapsed .rail-collapse span {
  opacity: 0;
  pointer-events: none;
}
.app-shell--collapsed .rail :deep(.logo-mark__wordmark) {
  width: 32px;
}
.topbar {
  position: fixed;
  inset: 0 0 auto var(--rail-width);
  z-index: 35;
  height: var(--top-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 0 20px;
  border-bottom: 1px solid var(--rule);
  background: rgba(255, 255, 255, 0.96);
}
.app-shell--collapsed .topbar {
  left: 72px;
}
.mobile-menu {
  display: none;
}
.topbar__actions {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.workspace-update-state {
  min-height: 30px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  color: var(--text-soft);
  background: var(--sheet-strong);
  font-size: 11px;
  font-weight: 650;
  white-space: nowrap;
}
.workspace-update-state--paused {
  border-color: color-mix(in srgb, var(--warning) 42%, var(--rule));
  color: var(--warning);
  background: var(--warning-soft);
}
.workspace-update-state__spinner {
  animation: workspace-update-spin 900ms linear infinite;
}
.workspace-update-state button {
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 5px;
  border-radius: 3px;
  color: inherit;
  background: color-mix(in srgb, var(--sheet) 66%, transparent);
  font: inherit;
  cursor: pointer;
}
.workspace-update-state button:hover:not(:disabled) {
  background: var(--sheet);
}
.workspace-update-state button:disabled {
  cursor: wait;
  opacity: 0.68;
}
@keyframes workspace-update-spin {
  to {
    transform: rotate(360deg);
  }
}
.demo-label {
  padding: 3px 7px;
  border-radius: 4px;
  color: #6f4b0d;
  background: var(--warning-soft);
  font-size: 11px;
  font-weight: 700;
}
.demo-label__compact {
  display: none;
}
.notification-button {
  position: relative;
}
.notification-button > span {
  position: absolute;
  top: 2px;
  right: 1px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: grid;
  place-items: center;
  border: 2px solid white;
  border-radius: 10px;
  color: white;
  background: var(--danger);
  font-size: 9px;
  font-weight: 750;
}
.profile-button {
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 3px 6px;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.profile-area {
  position: relative;
}
.profile-button:hover {
  background: var(--sheet-strong);
}
.profile-button__copy {
  display: grid;
  text-align: left;
}
.profile-button__copy strong {
  font-size: 12px;
}
.profile-button__copy small {
  color: var(--text-soft);
  font-size: 10px;
}
.profile-menu {
  position: absolute;
  top: 50px;
  right: 0;
  z-index: 60;
  width: 244px;
  padding: 8px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-md);
  background: white;
  box-shadow: var(--shadow-menu);
}
.profile-menu > div {
  display: grid;
  padding: 8px 9px 12px;
  border-bottom: 1px solid var(--rule);
}
.profile-menu small {
  color: var(--text-soft);
}
.profile-menu button,
.profile-menu a {
  width: 100%;
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 9px;
  border-radius: 5px;
  background: transparent;
  color: var(--text-soft);
  cursor: pointer;
  text-align: left;
}
.profile-menu button:hover,
.profile-menu a:hover {
  background: var(--sheet-strong);
  color: var(--text);
}
.app-main {
  min-height: calc(100vh - var(--top-height));
}
.app-main--project {
  height: calc(100vh - var(--top-height));
  overflow: hidden;
}
.scrim {
  position: fixed;
  inset: 0;
  z-index: 38;
  background: rgba(8, 27, 46, 0.45);
  cursor: pointer;
}
@media (max-width: 900px) {
  .app-shell,
  .app-shell--collapsed {
    padding-left: 0;
  }
  .rail,
  .app-shell--collapsed .rail {
    width: min(290px, 86vw);
    transform: translateX(-100%);
    transition: transform 180ms var(--ease);
  }
  .rail--mobile-open {
    transform: translateX(0);
  }
  .rail__mobile-close {
    display: inline-grid;
  }
  .app-shell--collapsed .rail :deep(.logo-mark__product),
  .app-shell--collapsed .rail-link span,
  .app-shell--collapsed .workspace-chip > span:last-child,
  .app-shell--collapsed .rail-collapse span {
    opacity: 1;
    pointer-events: auto;
  }
  .app-shell--collapsed .rail :deep(.logo-mark__wordmark) {
    width: 116px;
  }
  .rail-collapse {
    display: none;
  }
  .topbar,
  .app-shell--collapsed .topbar {
    left: 0;
    padding: 0 10px;
  }
  .mobile-menu {
    display: inline-grid;
  }
}
@media (max-width: 620px) {
  .workspace-update-state > span {
    display: none;
  }
  .profile-button__copy {
    display: none;
  }
  .demo-label {
    padding-inline: 6px;
  }
  .demo-label__wide {
    display: none;
  }
  .demo-label__compact {
    display: inline;
  }
}
</style>
